// === CanvasWorkflowPanel === docked right-side workflow graph (Phase B.1).
//
// Mounted beside the canvas as a third editor column. Reads the active
// InteractivePlugin box on the current scene, builds a React Flow graph
// (one ElementNode per item, plus default next-edges, branching-alternative
// edges, and section-workflow trigger edges), and provides bidirectional
// selection sync: clicking a node dispatches selectInteractiveItem so the
// canvas highlights the same element; selectedItemId from the
// interactivityStudio slice flows back here and is forwarded to React Flow
// via per-node `selected`.
//
// Lookup rules locked by the plan:
//   - Active InteractivePlugin box is found by scanning the scene's boxes
//     and matching pluginToolbarsById[boxId].pluginId === "InteractivePlugin"
//     (NOT by inspecting boxesById.content — that field is opaque).
//   - workflow.triggers live on the *section*. If the selected nav item is
//     a slide, walk up to its parent section; if the nav item is itself a
//     section, use it directly.
//
// The InteractivityStudio modal is untouched — this panel is the docked
// view-and-select surface; deep editing still happens in the modal.

import React, { useMemo, useState, useCallback, useEffect, useRef } from "react";
import i18n from "i18next";
import { connect } from "react-redux";
import {
  LuGitBranch,
  LuMap,
  LuCircleCheck,
  LuAlertTriangle,
  LuPlay,
  LuPause,
  LuSquare,
  LuPersonStanding,
  LuArrowLeft,
  LuArrowRight,
  LuX,
  LuLayoutGrid,
  LuRotateCcw,
  LuEye,
  LuEyeOff,
  LuChevronLeft,
  LuChevronRight,
} from "react-icons/lu";
import {
  ReactFlow,
  ReactFlowProvider,
  Background,
  Controls,
  MiniMap,
  applyNodeChanges,
} from "@xyflow/react";
import ElementNode from "./ElementNode";
import TimelineElementNode from "./TimelineElementNode";
import StartNode from "./StartNode";
import EndNode from "./EndNode";
import SceneNode from "./SceneNode";
import ScenarioElementNode from "./ScenarioElementNode";
import DeletableEdge from "./DeletableEdge";
import { buildElementGraph } from "./buildElementGraph";
import { buildScenarioGraph } from "./buildScenarioGraph";
import { formatTimeFromFrame } from "./formatTime";
import { validateWorkflow } from "./validateWorkflow";
import { applyDagreLayout } from "./applyDagreLayout";
import ValidationDrawer from "./ValidationDrawer";
import AiWorkflowGenerator from "./AiWorkflowGenerator";
import AiCourseGenerator from "./AiCourseGenerator";
import WorkflowExportImport from "./WorkflowExportImport";
import "./ValidationDrawer.scss";
import {
  selectInteractiveItem,
  addWorkflowTrigger,
  deleteWorkflowTrigger,
  setPreviewFrame,
  toggleCanvasWorkflowExpanded,
  toggleCanvasWorkflowMinimap,
  setWorkflowNodePosition,
  clearWorkflowNodePositions,
  setEdgeLabel,
  clearEdgeLabel,
  selectScene,
  selectScenarioElement,
} from "../../../common/actions";
import { resolveBoxTiming } from "../../../common/resolveBoxTiming.es6";
import "./CanvasWorkflowPanel.scss";
import "@xyflow/react/dist/style.css";

// Static reference so React Flow doesn't tear down internal caches on every
// render. Two custom node types: editable InteractivePlugin sub-item nodes
// (elementNode) and read-only timeline-level scene-box placeholders
// (timelineElementNode, Phase C Item 2).
const NODE_TYPES = {
  elementNode: ElementNode,
  timelineElementNode: TimelineElementNode,
  startNode: StartNode,
  endNode: EndNode,
  sceneNode: SceneNode,
  scenarioElementNode: ScenarioElementNode,
};

// Round 25 Item 0: every edge runs through DeletableEdge so trigger-derived
// edges can render a hover trashcan that dispatches deleteWorkflowTrigger.
// Edges without a data.triggerId render as plain smooth-step paths (the
// trashcan is suppressed inside DeletableEdge), so synthetic Start/End,
// timeline auto-edges, and default next-edges remain undeletable.
const EDGE_TYPES = {
  default: DeletableEdge,
};

// MiniMap node-color palette. Mirrors the kind-specific header backgrounds in
// ElementNode.scss so the overview reads as a desaturated bird's-eye of the
// real cards. Default elementNode kinds use the Studio primary tint; the two
// kinds with overridden header bands (BranchingQuestion amber, EndScreen
// coral) map to their respective hex values. Timeline element nodes pick up
// neutral gray (they're read-only scene-box placeholders). Start nodes and
// satisfied End nodes are green; warning End nodes are coral.
const MINIMAP_KIND_COLORS = {
  BranchingQuestion: "#fef3c7",
  EndScreen: "#fee2e2",
};
const MINIMAP_DEFAULT_KIND_COLOR = "#f4ebff"; // matches $primary-soft
const MINIMAP_TIMELINE_COLOR = "#dadce0"; // matches $border-default (gray)
const MINIMAP_START_COLOR = "#dcfce7"; // green-satisfied
const MINIMAP_END_SATISFIED_COLOR = "#dcfce7";
const MINIMAP_END_WARNING_COLOR = "#fee2e2"; // red-warning

function miniMapNodeColor(node) {
  if (!node) return MINIMAP_DEFAULT_KIND_COLOR;
  if (node.type === "startNode") return MINIMAP_START_COLOR;
  if (node.type === "endNode") {
    return node.data && node.data.satisfied
      ? MINIMAP_END_SATISFIED_COLOR
      : MINIMAP_END_WARNING_COLOR;
  }
  if (node.type === "timelineElementNode") return MINIMAP_TIMELINE_COLOR;
  // elementNode: kind-specific where defined, otherwise the default Studio
  // primary tint that ElementNode.scss applies to plain kinds.
  const kind = node.data && node.data.kind;
  return MINIMAP_KIND_COLORS[kind] || MINIMAP_DEFAULT_KIND_COLOR;
}

// Map a React Flow source handle id to a workflow.trigger.on value (plus any
// extras buildElementGraph needs to render the edge symmetrically). The
// outcome here is the same string buildElementGraph reads back via
// `tr.on.split(".")[1]`, so a freshly dispatched trigger immediately renders
// as a "correct"/"wrong" edge between the dragged nodes.
function handleToTriggerOn(handle) {
  if (handle === "wrong") return { on: "onAnswer.wrong" };
  if (handle === "correct") return { on: "onAnswer.correct" };
  // Round 25 Item 0: opt-<key> handles drive a per-option trigger so each
  // option in an MCQ / MultipleAnswer / YesNo / TrueFalse can route to a
  // different downstream node (botpress-style). The full handle id
  // (e.g. "opt-b") is preserved as optionKey so buildElementGraph can
  // re-emit the edge from the matching source handle without re-deriving it.
  if (typeof handle === "string" && handle.startsWith("opt-")) {
    return { on: "onOptionPicked", optionKey: handle };
  }
  if (typeof handle === "string" && handle.startsWith("alt-")) {
    return { on: "onBranchAnswer", alternativeId: handle.replace(/^alt-/, "") };
  }
  // Default ("next" handle, anything unknown) — treat as the generic
  // "correct" outcome so the user gets a visible edge in the panel.
  return { on: "onAnswer.correct" };
}

// Short opaque id matching the convention used elsewhere (WorkflowTab).
function randomTriggerId() {
  return `t_${Math.random().toString(36).slice(2, 8)}`;
}

function CanvasWorkflowPanel({
  docked,
  expanded,
  minimapVisible,
  items,
  scenario,
  boxId,
  sectionId,
  sceneId,
  triggers,
  selectedItemId,
  selectedSceneId,
  selectedElementId,
  boxStartFrame,
  sceneBoxIds,
  boxesById,
  pluginToolbarsById,
  nodePositions,
  edgeLabels,
  onSelectItem,
  onSelectScene,
  onSelectElement,
  onAddTrigger,
  onDeleteTrigger,
  onPreviewNode,
  onToggleExpanded,
  onToggleMinimap,
  onApplyFix,
  onSaveNodePosition,
  onResetNodePositions,
  onSetEdgeLabel,
  onClearEdgeLabel,
  mode,
}) {
  // Use scenario graph when scenario data is present (new architecture),
  // otherwise fall back to the legacy items-based graph.
  const isScenarioMode = !!(scenario && scenario.scenes && scenario.scenes.length > 0);
  const { nodes, edges } = useMemo(() => {
    if (isScenarioMode) {
      // Scenario graph: one sceneNode + scenarioElementNodes per internal scene,
      // plus a synthetic End with every terminal scene connected.
      const scenarioGraph = buildScenarioGraph(scenario);

      // Build the TL section (non-interactive scene boxes) using buildElementGraph
      // with empty items — only the start/end/TL nodes come back.
      const tlGraph = buildElementGraph([], [], {
        sceneBoxIds: sceneBoxIds || [],
        boxesById: boxesById || {},
        pluginToolbarsById: pluginToolbarsById || {},
        interactiveBoxId: boxId || null,
      });

      const startNodes = tlGraph.nodes.filter((n) => n.type === "startNode");
      const tlOnlyNodes = tlGraph.nodes.filter((n) => n.type === "timelineElementNode");
      // Keep the TL End node only when there are actual TL elements to connect to it.
      const tlEndNode =
        tlOnlyNodes.length > 0 ? (tlGraph.nodes.find((n) => n.type === "endNode") ?? null) : null;

      const tlNodes = [...startNodes, ...tlOnlyNodes, ...(tlEndNode ? [tlEndNode] : [])];

      // Each TL element is an independent parallel branch:
      //   Start → [TL element at startFrame] → [TL End]
      // This reflects how timeline boxes overlap and each has its own timing.
      const tlEdges = [];
      tlOnlyNodes.forEach((tlNode) => {
        // Label each inbound edge with the element's start time so authors
        // instantly see when in the scene timeline this box appears.
        const startTime =
          typeof tlNode.data.startFrame === "number"
            ? formatTimeFromFrame(tlNode.data.startFrame, 30) || "0:00"
            : null;
        tlEdges.push({
          id: `e:start-branch:${tlNode.data.boxId}`,
          source: "synthetic:start",
          target: tlNode.id,
          targetHandle: "in",
          label: startTime ? `@ ${startTime}` : "begin",
          animated: false,
        });
        if (tlEndNode) {
          const endTime =
            tlNode.data.endFrame != null
              ? formatTimeFromFrame(tlNode.data.endFrame, 30) || "end"
              : null;
          tlEdges.push({
            id: `e:tl-end:${tlNode.data.boxId}`,
            source: tlNode.id,
            target: tlEndNode.id,
            targetHandle: "in",
            label: endTime ? `@ ${endTime}` : "end",
            animated: false,
          });
        }
      });

      // Separate parallel branch: Start → scenario start scene.
      // Labelled with the InteractivePlugin box's resolved start time so
      // authors can see at which point in the scene the scenario kicks in.
      const startSceneId = scenario && scenario.startSceneId;
      const bridgeEdges = [];
      if (startSceneId) {
        const ipTimeLabel =
          typeof boxStartFrame === "number"
            ? `@ ${formatTimeFromFrame(boxStartFrame, 30) || "0:00"}`
            : "interactive";
        bridgeEdges.push({
          id: `e:start-to-scenario:${startSceneId}`,
          source: "synthetic:start",
          target: `sc:${startSceneId}`,
          label: ipTimeLabel,
          animated: false,
        });
      }

      // Place scenario section below the TL section + TL End node.
      // TL_ROW_H = NODE_H (110) + ROW_GAP (60) mirrors buildElementGraph.
      // tlEndY mirrors buildElementGraph's `endY + 40` formula:
      //   bottomRow = numTL, endY = numTL * ROW + 40, node.y = endY + 40 = numTL * 170 + 80.
      const TL_ROW_H = 170;
      const tlEndY = tlOnlyNodes.length > 0 ? tlOnlyNodes.length * TL_ROW_H + 80 : 0;
      const tlOffset = tlOnlyNodes.length > 0 ? tlEndY + 120 : 0;
      const shiftedNodes = scenarioGraph.nodes.map((n) => ({
        ...n,
        position: { x: n.position.x, y: n.position.y + tlOffset },
      }));

      return {
        nodes: [...tlNodes, ...shiftedNodes],
        edges: [...tlEdges, ...bridgeEdges, ...scenarioGraph.edges],
      };
    }
    return buildElementGraph(items || [], triggers || [], {
      sceneBoxIds: sceneBoxIds || [],
      boxesById: boxesById || {},
      pluginToolbarsById: pluginToolbarsById || {},
      interactiveBoxId: boxId || null,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isScenarioMode,
    scenario,
    items,
    triggers,
    sceneBoxIds,
    boxesById,
    pluginToolbarsById,
    boxId,
    boxStartFrame,
  ]);
  // Round 25 Item 0: inject an onDelete callback into the data bag of any
  // trigger-derived edge so DeletableEdge's trashcan handler can call back
  // into the panel with the right sectionId. We bind sectionId here (instead
  // of from the edge data) so the callback survives mid-flight sectionId
  // changes correctly: each render carries the current sectionId.
  const edgesWithDelete = useMemo(() => {
    const labels = edgeLabels || {};
    return edges.map((e) => {
      // Layer customLabel from the canvas_workflow.edgeLabels slice over the
      // trigger-derived label produced by buildElementGraph. The original
      // label stays in `e.label` so DeletableEdge can fall back to it when
      // the override is cleared (CLEAR_EDGE_LABEL or an empty-string commit).
      const customLabel = labels[e.id] != null ? labels[e.id] : null;
      // onSetLabel is wired for every edge — even synthetic ones (Start/End,
      // timeline auto-edges, default next-edges) — because the override is a
      // UI-only concern and shouldn't be gated on whether a trigger exists.
      // An empty-string commit clears the override (parent maps it to
      // clearEdgeLabel) so authors can revert to the derived label.
      const onSetLabel = (edgeId, label) => {
        if (!edgeId) return;
        if (label === "" || label == null) {
          onClearEdgeLabel(edgeId);
        } else {
          onSetEdgeLabel(edgeId, label);
        }
      };
      const baseData = e.data || {};
      const nextData = { ...baseData, customLabel, onSetLabel };
      if (baseData.triggerId) {
        nextData.onDelete = (triggerId) => {
          if (!hasId(sectionId)) return;
          onDeleteTrigger(sectionId, triggerId);
        };
      }
      return { ...e, data: nextData };
    });
  }, [edges, edgeLabels, sectionId, onDeleteTrigger, onSetEdgeLabel, onClearEdgeLabel]);
  // ── Timeline play status (mirrors IdeaTimeline.playing via CustomEvent) ──
  const [tlPlaying, setTlPlaying] = useState(false);
  useEffect(() => {
    const onStatus = (e) => setTlPlaying(!!(e.detail && e.detail.playing));
    document.addEventListener("aidea-timeline-status", onStatus);
    return () => document.removeEventListener("aidea-timeline-status", onStatus);
  }, []);

  // ── Workflow simulation mode ─────────────────────────────
  // Steps through the graph node-by-node along the first available edge.
  // simHistory tracks visited node ids so Back is possible.
  const [simMode, setSimMode] = useState(false);
  const [simNodeId, setSimNodeId] = useState(null);
  const simHistoryRef = useRef([]);

  const enterSimMode = () => {
    simHistoryRef.current = [];
    setSimNodeId("synthetic:start");
    setSimMode(true);
  };
  const exitSimMode = () => {
    simHistoryRef.current = [];
    setSimNodeId(null);
    setSimMode(false);
  };

  // nodeFrameRef holds a snapshot of node data for use inside simStep without
  // creating a circular dependency with nodesWithSel (defined later in the render).
  const nodeFrameRef = useRef({});

  const simStep = useCallback(
    (direction) => {
      if (!simNodeId) return;
      const allEdges = edgesWithDelete;
      if (direction === "next") {
        const outEdges = allEdges.filter((e) => e.source === simNodeId);
        if (outEdges.length === 0) return;
        const next = outEdges[0].target;
        simHistoryRef.current.push(simNodeId);
        setSimNodeId(next);
        const frame =
          nodeFrameRef.current[next] ?? (typeof boxStartFrame === "number" ? boxStartFrame : 0);
        onPreviewNode(frame, next);
      } else {
        const prev = simHistoryRef.current.pop();
        if (!prev) return;
        setSimNodeId(prev);
        const frame =
          nodeFrameRef.current[prev] ?? (typeof boxStartFrame === "number" ? boxStartFrame : 0);
        onPreviewNode(frame, prev);
      }
    },
    [simNodeId, edgesWithDelete, boxStartFrame, onPreviewNode]
  );

  // Round 28: dagre auto-layout overrides keyed by node id. Populated by the
  // header "Auto-layout" button; cleared whenever the structural shape of
  // the graph changes (different node id set) so a fresh build is laid out
  // from the builder's defaults instead of stale dagre coords.
  const [dagreOverrides, setDagreOverrides] = useState(null);
  const nodeIdsKey = useMemo(() => nodes.map((n) => n.id).join("|"), [nodes]);
  React.useEffect(() => {
    setDagreOverrides(null);
  }, [nodeIdsKey]);

  // Round 25 follow-up (closes user item 2): in-flight drag positions held in
  // local state so React Flow can move the node smoothly between pointerdown
  // and pointerup. On drag-end we promote the final {x, y} to the redux slice
  // (canvas_workflow.nodePositions) via onSaveNodePosition; the next render
  // then reads it back through nodePositions and the local copy clears itself
  // (it's keyed off the node id and the slice wins via lookup order below).
  const [liveDragPositions, setLiveDragPositions] = useState({});

  // Project selectedItemId from the interactivityStudio slice onto each node.
  // React Flow renders the `selected` flag as the node's outline state, which
  // ElementNode.scss styles via .canvas-wf-node--selected. Timeline nodes
  // carry boxId (not itemId), so they never light up via selectedItemId —
  // selection there is driven by the canvas's box selection (Item 6 picks
  // that up separately).
  //
  // Position lookup order (last wins): builder default -> dagre overrides ->
  // persisted nodePositions slice -> live in-flight drag. The slice trumps
  // dagre so a saved hand-tune isn't blown away the next time Auto-layout
  // fires for an unrelated node; "Reset positions" clears the slice and
  // restores the builder/dagre layout.
  const nodesWithSel = useMemo(() => {
    const saved = nodePositions || {};
    const frameMap = {};
    const mapped = nodes.map((n) => {
      let position = n.position;
      if (dagreOverrides && dagreOverrides[n.id]) position = dagreOverrides[n.id];
      if (saved[n.id]) position = saved[n.id];
      if (liveDragPositions[n.id]) position = liveDragPositions[n.id];
      // Populate frame lookup for simulation step.
      if (n.data && typeof n.data.startFrame === "number") {
        frameMap[n.id] = n.data.startFrame;
      }
      const isSelected = isScenarioMode
        ? n.type === "scenarioElementNode"
          ? n.data && n.data.elementId === selectedElementId
          : n.data && n.data.sceneId === selectedSceneId
        : n.data && n.data.itemId === selectedItemId;
      return {
        ...n,
        position,
        selected: !!isSelected,
        className: simMode && n.id === simNodeId ? "canvas-wf-node--sim-active" : undefined,
      };
    });
    nodeFrameRef.current = frameMap;
    return mapped;
  }, [
    nodes,
    selectedItemId,
    selectedSceneId,
    selectedElementId,
    isScenarioMode,
    dagreOverrides,
    nodePositions,
    liveDragPositions,
    simMode,
    simNodeId,
  ]);

  const handleAutoLayout = () => {
    const laid = applyDagreLayout(nodes, edges);
    const map = {};
    for (const n of laid) map[n.id] = n.position;
    setDagreOverrides(map);
  };

  // React Flow drag-in-progress: each "position" change carries the latest
  // {x, y}. We hold it in liveDragPositions so the node tracks the cursor;
  // dispatching once per pixel into redux would be wasteful and would flood
  // the action log. The final position is committed on drag-end (below).
  const handleNodesChange = useCallback(
    (changes) => {
      setLiveDragPositions((prev) => {
        let next = prev;
        for (const ch of changes) {
          if (ch.type === "position" && ch.position) {
            if (next === prev) next = { ...prev };
            next[ch.id] = { x: ch.position.x, y: ch.position.y };
          }
        }
        return next;
      });
      // applyNodeChanges is also called for completeness — React Flow uses the
      // returned array internally to drive selection + dimension changes; we
      // re-derive positions from state on the next render so the return value
      // is informational only here.
      applyNodeChanges(changes, nodesWithSel);
    },
    [nodesWithSel]
  );

  // Drag-end: promote the live position to the redux slice and clear the
  // in-flight entry so the next render reads from nodePositions (single
  // source of truth across page reloads / store rehydration).
  const handleNodeDragStop = useCallback(
    (_evt, node) => {
      if (!node || !node.id || !node.position) return;
      onSaveNodePosition(node.id, node.position);
      setLiveDragPositions((prev) => {
        if (!prev[node.id]) return prev;
        const next = { ...prev };
        delete next[node.id];
        return next;
      });
    },
    [onSaveNodePosition]
  );

  const handleResetPositions = () => {
    setDagreOverrides(null);
    setLiveDragPositions({});
    onResetNodePositions();
  };

  // Deterministic Workflow Validator (separate from the AI Pedagogy Reviewer).
  // Runs every render against the same (items, triggers) pair the graph
  // builder consumes — pure function, microsecond-cheap, so memoising is
  // belt-and-braces against pathological item counts.
  const issues = useMemo(() => validateWorkflow(items || [], triggers || []), [items, triggers]);
  const [showValidator, setShowValidator] = useState(false);
  const issueCount = issues.length;
  const badgeClassName =
    issueCount === 0
      ? "canvas-wf-validator-badge canvas-wf-validator-badge--clean"
      : "canvas-wf-validator-badge";
  const BadgeIcon = issueCount === 0 ? LuCircleCheck : LuAlertTriangle;
  const badgeLabel =
    issueCount === 0 ? "no issues" : `${issueCount} issue${issueCount === 1 ? "" : "s"}`;
  // Clicking a row in the drawer dispatches the same selectInteractiveItem
  // path the ReviewDrawer uses, so the canvas highlights the same node.
  const focusValidationItem = (itemId) => {
    if (!itemId || !boxId) return;
    onSelectItem(boxId, itemId);
  };

  // Modal mode bypasses the docked gating.
  const isModal = mode === "modal";

  // Only hide when the panel has been explicitly collapsed (docked=false).
  // A missing InteractivePlugin box is no longer a reason to hide — every
  // scene now shows the workflow with at minimum its timeline element nodes.
  if (!isModal && !docked) return null;

  // Round 27 Item 11: in docked mode, an expanded class doubles the panel
  // width via SCSS. Modal mode ignores the toggle — it already fills the
  // modal body edge-to-edge.
  const rootClassName = isModal
    ? "canvas-workflow-panel canvas-workflow-panel--modal"
    : expanded
      ? "canvas-workflow-panel canvas-workflow-panel--expanded"
      : "canvas-workflow-panel";

  return (
    <aside className={rootClassName} role="complementary" aria-label="Workflow">
      {!isModal && (
        <button
          type="button"
          className="canvas-workflow-panel__expand-toggle"
          onClick={onToggleExpanded}
          aria-label={expanded ? "Collapse workflow panel" : "Expand workflow panel"}
          aria-pressed={expanded}
          title={expanded ? "Collapse workflow panel" : "Expand workflow panel"}
        >
          {expanded ? <LuChevronRight size={18} /> : <LuChevronLeft size={18} />}
        </button>
      )}
      {!isModal && (
        <header className="canvas-workflow-panel__head">
          {isScenarioMode ? (
            <LuMap size={18} className="canvas-workflow-panel__head-icon" />
          ) : (
            <LuGitBranch size={18} className="canvas-workflow-panel__head-icon" />
          )}
          <span className="canvas-workflow-panel__head-title">
            {isScenarioMode ? "Senaryo Grafı" : "Workflow"}
          </span>
          <div className="canvas-workflow-panel__head-actions">
            <button
              type="button"
              className={badgeClassName}
              onClick={() => setShowValidator((s) => !s)}
              aria-label={`Workflow validator — ${badgeLabel}`}
              aria-pressed={showValidator}
              title={`Workflow validator — ${badgeLabel}`}
              data-testid="canvas-wf-validator-badge"
            >
              <BadgeIcon size={14} />
              <span>{badgeLabel}</span>
            </button>
            <span className="canvas-workflow-panel__head-sep" />
            {!simMode && (
              <>
                <button
                  type="button"
                  className={`canvas-workflow-panel__play-btn${tlPlaying ? " canvas-workflow-panel__play-btn--playing" : ""}`}
                  onClick={() => document.dispatchEvent(new CustomEvent("aidea-timeline-play"))}
                  aria-label={tlPlaying ? "Pause timeline" : "Play timeline"}
                  aria-pressed={tlPlaying}
                  title={tlPlaying ? "Pause timeline" : "Play timeline"}
                  data-testid="canvas-wf-play"
                >
                  {tlPlaying ? <LuPause size={16} /> : <LuPlay size={16} />}
                </button>
                <button
                  type="button"
                  className="canvas-workflow-panel__stop-btn"
                  onClick={() => document.dispatchEvent(new CustomEvent("aidea-timeline-stop"))}
                  aria-label="Stop timeline"
                  title="Stop timeline"
                  data-testid="canvas-wf-stop"
                  disabled={!tlPlaying}
                >
                  <LuSquare size={16} />
                </button>
                <button
                  type="button"
                  className="canvas-workflow-panel__sim-btn"
                  onClick={enterSimMode}
                  aria-label="Simulate workflow"
                  title="Step through workflow nodes"
                  data-testid="canvas-wf-simulate"
                >
                  <LuPersonStanding size={16} />
                </button>
              </>
            )}
            {simMode && (
              <div className="canvas-workflow-panel__sim-controls">
                <button
                  type="button"
                  className="canvas-workflow-panel__sim-nav"
                  onClick={() => simStep("back")}
                  disabled={simHistoryRef.current.length === 0}
                  aria-label="Previous step"
                  title="Previous step"
                >
                  <LuArrowLeft size={14} />
                </button>
                <span className="canvas-workflow-panel__sim-label">
                  {simNodeId
                    ? (() => {
                        const n = nodesWithSel.find((x) => x.id === simNodeId);
                        return (n && n.data && (n.data.label || n.data.name)) || simNodeId;
                      })()
                    : "—"}
                </span>
                <button
                  type="button"
                  className="canvas-workflow-panel__sim-nav"
                  onClick={() => simStep("next")}
                  disabled={!edgesWithDelete.some((e) => e.source === simNodeId)}
                  aria-label="Next step"
                  title="Next step"
                >
                  <LuArrowRight size={14} />
                </button>
                <button
                  type="button"
                  className="canvas-workflow-panel__sim-exit"
                  onClick={exitSimMode}
                  aria-label="Exit simulation"
                  title="Exit simulation"
                >
                  <LuX size={13} />
                </button>
              </div>
            )}
            <span className="canvas-workflow-panel__head-sep" />
            <button
              type="button"
              className="canvas-workflow-panel__auto-layout"
              onClick={handleAutoLayout}
              aria-label="Auto-layout nodes"
              title="Auto-layout nodes (top-down)"
              data-testid="canvas-wf-auto-layout"
            >
              <LuLayoutGrid size={16} />
            </button>
            <button
              type="button"
              className="canvas-workflow-panel__reset-positions"
              onClick={handleResetPositions}
              aria-label="Reset node positions"
              title="Reset node positions to defaults"
              data-testid="canvas-wf-reset-positions"
            >
              <LuRotateCcw size={16} />
            </button>
            <button
              type="button"
              className="canvas-workflow-panel__minimap-toggle"
              onClick={onToggleMinimap}
              aria-label={minimapVisible ? "Hide minimap" : "Show minimap"}
              aria-pressed={minimapVisible}
              title="Toggle minimap"
              data-testid="canvas-wf-minimap-toggle"
            >
              {minimapVisible ? <LuEye size={16} /> : <LuEyeOff size={16} />}
            </button>
            <AiWorkflowGenerator sectionId={sectionId} />
            <AiCourseGenerator sectionId={sectionId} />
            <WorkflowExportImport
              sectionId={sectionId}
              sceneId={sceneId}
              boxId={boxId}
              items={items}
              triggers={triggers}
            />
          </div>
        </header>
      )}
      <div className="canvas-workflow-panel__body">
        <ReactFlowProvider>
          <ReactFlow
            nodes={nodesWithSel}
            edges={edgesWithDelete}
            nodeTypes={NODE_TYPES}
            edgeTypes={EDGE_TYPES}
            onNodesChange={handleNodesChange}
            onNodeDragStop={handleNodeDragStop}
            onNodeClick={(_e, node) => {
              // Structural Start / End nodes are read-only markers — no selection action.
              if (node.type === "startNode" || node.type === "endNode") return;
              // Scenario scene nodes (new architecture)
              if (node.type === "sceneNode") {
                if (node.data && node.data.sceneId && boxId) {
                  onSelectScene(boxId, node.data.sceneId);
                }
                return;
              }
              // Scenario element nodes — select scene + element
              if (node.type === "scenarioElementNode") {
                if (node.data && node.data.sceneId && node.data.elementId && boxId) {
                  onSelectScene(boxId, node.data.sceneId);
                  onSelectElement(boxId, node.data.sceneId, node.data.elementId);
                }
                return;
              }
              // Timeline element nodes (Phase C Item 2) carry boxId + no itemId.
              if (node.type === "timelineElementNode") {
                const tlBoxId = node.data && node.data.boxId;
                if (tlBoxId) onSelectItem(tlBoxId, null);
                const tlStart = node.data && node.data.startFrame;
                const previewFrame = typeof tlStart === "number" ? tlStart : 0;
                onPreviewNode(previewFrame, `tl:${tlBoxId}`);
                return;
              }
              onSelectItem(boxId, node.data.itemId);
              // === Phase C item 6 — hard-hide canvas preview ===
              // Nodes in this branch are InteractivePlugin *sub-items* whose
              // `data.item` carries no top-level timeline; the scrub anchor is
              // the parent InteractivePlugin box's resolved startFrame. Falling
              // back to 0 (scene origin) when the box has no gate keeps the
              // preview useful — every always-visible box still renders.
              const itemStart =
                node.data && node.data.item && node.data.item.timeline
                  ? node.data.item.timeline.startFrame
                  : undefined;
              const frame =
                typeof itemStart === "number"
                  ? itemStart
                  : typeof boxStartFrame === "number"
                    ? boxStartFrame
                    : 0;
              onPreviewNode(frame, node.data && node.data.itemId);
            }}
            onConnect={(conn) => {
              // Scenario mode: connections are display-only (navigate actions
              // are edited per-element in the scene panel). Skip trigger dispatch.
              if (isScenarioMode) return;
              // conn = { source: "el:<itemId>", sourceHandle, target: "el:<itemId>", targetHandle }.
              // Drag from a node's right-side handle to another node's left "in" handle
              // creates a section.workflow.triggers entry. Section + scene + box come
              // from the connected props; the handle picks the trigger.on value.
              if (!hasId(sectionId) || !conn || !conn.source || !conn.target) return;
              // Round 24 Item 4: timeline element node connections (tl:<boxId> →
              // tl:<boxId>) are visual only — the trigger model only routes
              // sub-item events. Allow the gesture to complete without
              // dispatching a malformed trigger; React Flow's internal edge
              // state will hold the connection for this session.
              if (conn.source.startsWith("tl:") || conn.target.startsWith("tl:")) return;
              const fromItemId = conn.source.replace(/^el:/, "");
              const toItemId = conn.target.replace(/^el:/, "");
              if (!fromItemId || !toItemId || fromItemId === toItemId) return;
              const mapped = handleToTriggerOn(conn.sourceHandle);
              const trigger = {
                id: randomTriggerId(),
                sceneId: hasId(sceneId) ? sceneId : null,
                pluginBoxId: boxId || null,
                sourceItemId: fromItemId,
                on: mapped.on,
                actionTargetItemId: toItemId,
                actions: [],
              };
              if (mapped.alternativeId) trigger.alternativeId = mapped.alternativeId;
              if (mapped.optionKey) trigger.optionKey = mapped.optionKey;
              onAddTrigger(sectionId, trigger);
            }}
            fitView
            proOptions={{ hideAttribution: true }}
          >
            <Background gap={16} />
            <Controls showInteractive={false} />
            {/* MiniMap overview window. Mounted bottom-right. Hidden when the
                panel is collapsed (narrow) so it doesn't crowd the limited
                graph area — modal mode always shows it because it fills the
                whole modal body. Also gated by canvas_workflow.minimapVisible
                so authors can opt out via the header eye toggle. */}
            {minimapVisible && (isModal || expanded) && (
              <MiniMap
                position="bottom-right"
                nodeColor={miniMapNodeColor}
                pannable
                zoomable
                ariaLabel="Workflow minimap"
                data-testid="canvas-wf-minimap"
              />
            )}
            {nodesWithSel.length === 0 && (
              <div className="canvas-workflow-panel__empty">
                <LuGitBranch size={40} style={{ opacity: 0.25 }} />
                <p>{i18n.t("Workflow.EmptyScene") || "Bu sahnede henüz içerik yok."}</p>
                <p>
                  {i18n.t("Workflow.EmptySceneHint") || "İçerik ekledikçe workflow burada görünür."}
                </p>
              </div>
            )}
          </ReactFlow>
        </ReactFlowProvider>
        <ValidationDrawer
          show={!isModal && showValidator}
          issues={issues}
          onClose={() => setShowValidator(false)}
          onFocusItem={focusValidationItem}
          onApplyFix={onApplyFix}
          fixContext={{
            boxId: boxId || null,
            sectionId: hasId(sectionId) ? sectionId : null,
            sceneId: hasId(sceneId) ? sceneId : null,
            items: items || [],
            triggers: triggers || [],
          }}
        />
      </div>
    </aside>
  );
}

// Find the InteractivePlugin box on the active scene by checking the plugin
// id stored in pluginToolbarsById — boxesById.content is an opaque per-plugin
// payload, so we deliberately route around it (matches the lookup fix in
// commit c8347b54).
function findInteractiveBoxId(scene, pluginsById) {
  if (!scene || !Array.isArray(scene.boxes)) return null;
  for (const bId of scene.boxes) {
    const tb = pluginsById[bId];
    if (tb && tb.pluginId === "InteractivePlugin") return bId;
  }
  return null;
}

// A section id may be `0` (course root) — strictly null/undefined/"" means missing.
// Mirrors the helper in selectSectionWorkflow.js (commit 09debbdd). Without
// this, the OR-fallback `scene.parent || null` would discard a numeric 0 parent
// and the panel would re-read `triggers=[]`, so newly-added trigger edges on
// the course root never render.
const hasId = (v) => v !== null && v !== undefined && v !== "";

// Section resolution: workflow triggers live on the section. If the selected
// nav item *is* a section, use it directly; if it is a slide, walk up via
// .parent. Defensive against missing/orphan nav items.
function resolveSectionId(navItemSelected, scene) {
  if (!scene) return null;
  if (scene.type === "section") return navItemSelected;
  return hasId(scene.parent) ? scene.parent : null;
}

const mapState = (state) => {
  const present = (state.undoGroup && state.undoGroup.present) || {};
  const navItemSelected = present.navItemSelected;
  const navItemsById = present.navItemsById || {};
  const scene = navItemSelected ? navItemsById[navItemSelected] : null;
  const pluginsById = present.pluginToolbarsById || {};
  const boxesById = present.boxesById || {};
  const animationsById = present.animationsById || {};
  const boxId = findInteractiveBoxId(scene, pluginsById);
  const pluginToolbar = boxId ? pluginsById[boxId] : null;
  const items = (pluginToolbar && pluginToolbar.state && pluginToolbar.state.items) || [];
  const scenario = (pluginToolbar && pluginToolbar.state && pluginToolbar.state.scenario) || null;
  const sectionId = resolveSectionId(navItemSelected, scene);
  const section = sectionId != null ? navItemsById[sectionId] : null;
  const triggers = (section && section.workflow && section.workflow.triggers) || [];
  // === Phase C item 6 — parent box's resolved startFrame ===
  // Used as the preview-frame fallback when a clicked sub-item has no timing.
  // Reads through the canonical resolveBoxTiming() so the panel agrees with
  // the timeline + visor on what "this box starts at" means.
  let boxStartFrame = null;
  if (boxId && boxesById[boxId]) {
    const t = resolveBoxTiming(boxesById[boxId], animationsById[boxId] || []);
    boxStartFrame = typeof t.startFrame === "number" ? t.startFrame : 0;
  }
  // === Phase C Item 2: scene box ids + lookup tables for timeline element nodes.
  // The builder consumes these to emit one read-only timelineElementNode per
  // non-InteractivePlugin scene box. We pass the lookup tables verbatim (not
  // a pre-filtered array) so the builder owns the exclusion/timing logic.
  const sceneBoxIds = scene && Array.isArray(scene.boxes) ? scene.boxes : [];
  return {
    // Default to docked=true so the panel still mounts if the
    // canvasWorkflow slice hasn't been wired into the root reducer yet
    // (Task 4 registers it).
    docked: state.canvasWorkflow ? state.canvasWorkflow.docked : true,
    // Round 27 Item 11: panel renders at double width when expanded.
    expanded: state.canvasWorkflow ? !!state.canvasWorkflow.expanded : false,
    // MiniMap visibility — default true so authors discover it. The panel's
    // header eye-icon toggles it; the body gates the actual <MiniMap /> mount
    // on this flag combined with the expanded state.
    minimapVisible: state.canvasWorkflow ? state.canvasWorkflow.minimapVisible !== false : true,
    boxId: boxId || null,
    // sectionId + sceneId are needed by onConnect to build the trigger.
    // sceneId is the currently selected nav item (slide or section); for a
    // section-level nav selection, sceneId === sectionId, which matches what
    // WorkflowTab does when it falls back from "current scene" to the section.
    // Use hasId() so a numeric 0 (course root) survives — `||` would drop it.
    sectionId: hasId(sectionId) ? sectionId : null,
    sceneId: hasId(navItemSelected) ? navItemSelected : null,
    items,
    scenario,
    triggers,
    selectedItemId: state.interactivityStudio ? state.interactivityStudio.selectedItemId : null,
    selectedSceneId: state.interactivityStudio ? state.interactivityStudio.selectedSceneId : null,
    selectedElementId: state.interactivityStudio
      ? state.interactivityStudio.selectedElementId
      : null,
    boxStartFrame,
    sceneBoxIds,
    boxesById,
    pluginToolbarsById: pluginsById,
    // Round 25 follow-up (closes user item 2): persisted node positions
    // keyed by node id (e.g. "el:<itemId>", "tl:<boxId>"). The panel layers
    // these over builder defaults so a drag survives item edits and rebuilds.
    nodePositions: (state.canvasWorkflow && state.canvasWorkflow.nodePositions) || {},
    // Inline edge-label overrides: a UI-only per-edge label map (double-click
    // an edge to edit). Layered on top of trigger-derived labels in
    // edgesWithDelete so the trigger model itself is never mutated.
    edgeLabels: (state.canvasWorkflow && state.canvasWorkflow.edgeLabels) || {},
  };
};

const mapDispatch = (dispatch) => ({
  onSelectItem: (boxId, itemId) => dispatch(selectInteractiveItem(boxId, itemId)),
  onSelectScene: (boxId, sceneId) => dispatch(selectScene(boxId, sceneId)),
  onSelectElement: (boxId, sceneId, elementId) =>
    dispatch(selectScenarioElement(boxId, sceneId, elementId)),
  onAddTrigger: (sectionId, trigger) => dispatch(addWorkflowTrigger(sectionId, trigger)),
  onDeleteTrigger: (sectionId, triggerId) => dispatch(deleteWorkflowTrigger(sectionId, triggerId)),
  onPreviewNode: (frame, sourceNodeId) => dispatch(setPreviewFrame(frame, sourceNodeId)),
  onToggleExpanded: () => dispatch(toggleCanvasWorkflowExpanded()),
  onToggleMinimap: () => dispatch(toggleCanvasWorkflowMinimap()),
  // Validator-fix thunks are produced by suggestedFix.actionCreator in
  // validateWorkflow.js. We just hand them to dispatch — redux-thunk routes
  // them as plain function actions, dispatching the inner ADD_INTERACTIVE_ITEM
  // / ADD_WORKFLOW_TRIGGER / DELETE_WORKFLOW_TRIGGER actions. After the
  // dispatch the connected `issues` memo re-runs against the new state and
  // the drawer re-renders without the resolved issue.
  onApplyFix: (thunkOrAction) => dispatch(thunkOrAction),
  onSaveNodePosition: (nodeId, position) => dispatch(setWorkflowNodePosition(nodeId, position)),
  onResetNodePositions: () => dispatch(clearWorkflowNodePositions()),
  onSetEdgeLabel: (edgeId, label) => dispatch(setEdgeLabel(edgeId, label)),
  onClearEdgeLabel: (edgeId) => dispatch(clearEdgeLabel(edgeId)),
});

export default connect(mapState, mapDispatch)(CanvasWorkflowPanel);
